/**
 * Finite state machine for the candidate pool import flow (phase 3A).
 *
 * Purpose
 * -------
 * Users reported that clicking "Upload CV" looked like nothing was happening
 * during the 10-20s Azure OpenAI parse. This reducer drives a progress panel
 * that walks through an optimistic 4-step rail (Uploaded -> Extracted ->
 * Analyzing -> Saving), shows elapsed time, and surfaces a rich success or
 * error toast.
 *
 * States / transitions
 * --------------------
 *   idle       --start-->       uploading
 *   uploading  --requestInFlight--> parsing   (optimistic: flipped as soon as
 *                                             fetch() has been handed the body)
 *   parsing    --beforeResponse--> saving    (optimistic: flipped ~1.5s before
 *                                             the typical Azure response)
 *   saving     --success-->      success
 *   any of the in-flight states --fail--> error
 *   success | error --reset--> idle
 *   any in-flight state --cancel--> error (kind="cancelled")
 *
 * The steps are intentionally optimistic: we do NOT have server-sent events,
 * so we drive them purely from the elapsed timer. This is safe UX — the
 * actual backend ordering is always upload -> extract -> parse -> persist,
 * so the only lie is the exact boundary between steps.
 */

export type ImportMode = 'cv' | 'paste';

export type ImportPhase =
  | 'idle'
  | 'uploading'
  | 'parsing'
  | 'saving'
  | 'success'
  | 'error';

/**
 * Subset of the backend PersistResult that the success toast displays.
 * Keeping it narrow so this module doesn't depend on the full type.
 */
export interface ImportSuccessSummary {
  person_id: number;
  name: string | null;
  title: string | null;
  primary_location: string | null;
  seniority_level: string | null;
  years_experience_total: number | null;
  experience_rows: number;
  project_rows: number;
  skill_rows: number;
  skill_phrase_rows: number;
  language_rows: number;
  review_status: string;
  parser_version: string;
}

export type ImportErrorKind =
  | 'timeout'
  | 'size'
  | 'format'
  | 'auth'
  | 'access'
  | 'server'
  | 'network'
  | 'cancelled'
  | 'unknown';

export interface ImportError {
  kind: ImportErrorKind;
  message: string;
  canRetry: boolean;
}

export interface ImportState {
  phase: ImportPhase;
  mode: ImportMode | null;
  fileName: string | null;
  startedAt: number | null;
  elapsedMs: number;
  success?: ImportSuccessSummary;
  error?: ImportError;
}

export const INITIAL_IMPORT_STATE: ImportState = {
  phase: 'idle',
  mode: null,
  fileName: null,
  startedAt: null,
  elapsedMs: 0,
};

export type ImportAction =
  | { type: 'start'; mode: ImportMode; fileName: string | null; startedAt: number }
  | { type: 'advance_to_parsing' }
  | { type: 'advance_to_saving' }
  | { type: 'tick'; now: number }
  | { type: 'success'; summary: ImportSuccessSummary }
  | { type: 'fail'; error: ImportError }
  | { type: 'cancel' }
  | { type: 'reset' };

export function importReducer(state: ImportState, action: ImportAction): ImportState {
  switch (action.type) {
    case 'start':
      return {
        phase: 'uploading',
        mode: action.mode,
        fileName: action.fileName,
        startedAt: action.startedAt,
        elapsedMs: 0,
      };
    case 'advance_to_parsing':
      if (state.phase !== 'uploading') return state;
      return { ...state, phase: 'parsing' };
    case 'advance_to_saving':
      if (state.phase !== 'parsing') return state;
      return { ...state, phase: 'saving' };
    case 'tick':
      if (state.startedAt == null) return state;
      if (
        state.phase === 'idle' ||
        state.phase === 'success' ||
        state.phase === 'error'
      ) {
        return state;
      }
      return { ...state, elapsedMs: Math.max(0, action.now - state.startedAt) };
    case 'success':
      return {
        ...state,
        phase: 'success',
        success: action.summary,
        error: undefined,
      };
    case 'fail':
      return {
        ...state,
        phase: 'error',
        error: action.error,
        success: undefined,
      };
    case 'cancel':
      if (
        state.phase === 'idle' ||
        state.phase === 'success' ||
        state.phase === 'error'
      ) {
        return state;
      }
      return {
        ...state,
        phase: 'error',
        error: { kind: 'cancelled', message: 'Import cancelled.', canRetry: true },
        success: undefined,
      };
    case 'reset':
      return { ...INITIAL_IMPORT_STATE };
    default:
      return state;
  }
}

/**
 * Classify any unexpected error or HTTP failure into a user-friendly
 * ImportError. Kept pure so it is trivially unit-testable.
 */
export function classifyImportError(
  err: unknown,
  httpStatus?: number | null
): ImportError {
  const rawMessage =
    err instanceof Error ? err.message : typeof err === 'string' ? err : 'Import failed';
  const lower = rawMessage.toLowerCase();

  if (err instanceof DOMException && err.name === 'AbortError') {
    return { kind: 'cancelled', message: 'Import cancelled.', canRetry: true };
  }
  if (
    lower.includes('failed to fetch') ||
    lower.includes('network') ||
    lower.includes('networkerror')
  ) {
    return {
      kind: 'network',
      message: 'Network error. Check your connection and retry.',
      canRetry: true,
    };
  }
  if (lower.includes('timeout') || lower.includes('timed out') || httpStatus === 504) {
    return {
      kind: 'timeout',
      message: 'Parsing timed out. Retry, or the CV may be too long.',
      canRetry: true,
    };
  }
  if (httpStatus === 401) {
    return {
      kind: 'auth',
      message: 'Session expired. Please sign in again.',
      canRetry: false,
    };
  }
  if (httpStatus === 403) {
    return {
      kind: 'access',
      message: 'You do not have permission to import into this pool.',
      canRetry: false,
    };
  }
  if (httpStatus === 404) {
    return {
      kind: 'access',
      message: 'Pool not found or no longer accessible.',
      canRetry: false,
    };
  }
  if (lower.includes('too large') || lower.includes('size') || httpStatus === 413) {
    return {
      kind: 'size',
      message: rawMessage || 'File is too large.',
      canRetry: false,
    };
  }
  if (
    lower.includes('unsupported') ||
    lower.includes('format') ||
    lower.includes('extract')
  ) {
    return {
      kind: 'format',
      message: rawMessage || 'Unsupported file format.',
      canRetry: false,
    };
  }
  if (httpStatus != null && httpStatus >= 500) {
    return {
      kind: 'server',
      message: rawMessage || 'Server error while importing.',
      canRetry: true,
    };
  }
  return { kind: 'unknown', message: rawMessage, canRetry: true };
}

/** Formats elapsed milliseconds as mm:ss for the in-flight banner. */
export function formatElapsed(ms: number): string {
  const safe = Math.max(0, ms);
  const total = Math.floor(safe / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Step index (0..3) for the 4-step rail from the current phase. */
export function stepIndexFor(phase: ImportPhase): number {
  switch (phase) {
    case 'uploading':
      return 0;
    case 'parsing':
      return 2;
    case 'saving':
      return 3;
    case 'success':
      return 3;
    default:
      return -1;
  }
}
